import type { Locale } from "@/lib/i18n/config";

export function formatBoolean(
  value: boolean | undefined | null,
  labels: { trueLabel: string; falseLabel: string },
) {
  if (value === undefined || value === null) {
    return "N/A";
  }

  return value ? labels.trueLabel : labels.falseLabel;
}

export function calculateSuspiciousIpRiskScore(security?: {
  isProxy?: boolean;
  isTor?: boolean;
  isBot?: boolean;
  isHosting?: boolean;
  isRelay?: boolean;
  vpn_or_proxy?: boolean;
  tor?: boolean;
  bot?: boolean;
  hosting?: boolean;
  relay?: boolean;
}) {
  const flags = {
    isProxy: security?.isProxy === true || security?.vpn_or_proxy === true,
    isTor: security?.isTor === true || security?.tor === true,
    isBot: security?.isBot === true || security?.bot === true,
    isHosting: security?.isHosting === true || security?.hosting === true,
    isRelay: security?.isRelay === true || security?.relay === true,
  };

  let result = 0;

  if (flags.isProxy || flags.isTor || flags.isBot || flags.isHosting) {
    result = 70;
  } else if (flags.isRelay) {
    result = 14;
  }

  if (flags.isProxy) result += 5;
  if (flags.isTor) result += 5;
  if (flags.isBot) result += 5;
  if (flags.isHosting) result += 3;

  return result;
}

export function getRiskScoreTone(score: number) {
  if (score > 50) {
    return "danger" as const;
  }

  if (score > 20) {
    return "warning" as const;
  }

  return "success" as const;
}

export type EmailScoringFlags = {
  is_valid?: boolean;
  is_free?: boolean;
  is_role_based?: boolean;
  is_disposable?: boolean;
  is_educational?: boolean;
  domain_is_new?: boolean;
};

/** 0 = low risk, 100 = high risk (aligned with IP tool risk badges). */
export function calculateEmailRiskScore(flags: EmailScoringFlags): number {
  if (flags.is_valid === false) {
    return 100;
  }

  if (flags.is_disposable) {
    return 92;
  }

  if (flags.domain_is_new) {
    return 62;
  }

  if (flags.is_free) {
    return 14;
  }

  return 6;
}

export function getEmailRiskVerdict(
  score: number,
): "low" | "moderate" | "high" {
  if (score > 50) {
    return "high";
  }

  if (score > 20) {
    return "moderate";
  }

  return "low";
}

export function normalizeAsn(value: string) {
  const trimmed = value.trim().toUpperCase();
  if (!trimmed) return "";
  return /^AS\d+$/.test(trimmed) ? trimmed : `AS${trimmed.replace(/^AS/, "")}`;
}

export function buildEmailConsideration(
  data: {
    is_free?: boolean;
    is_disposable?: boolean;
    is_role_based?: boolean;
  },
  locale: Locale,
) {
  if (locale === "ar") {
    const free = data.is_free ? "مجاني و " : "مخصص و ";
    const disposable = data.is_disposable ? "مؤقت و " : "دائمي و ";
    const role = data.is_role_based ? "تجاري" : "غير تجاري";
    return `${free}${disposable}${role}`;
  }

  if (locale === "zh") {
    const free = data.is_free ? "免费，" : "自定义，";
    const disposable = data.is_disposable ? "一次性，" : "非一次性，";
    const role = data.is_role_based ? "角色型" : "非角色型";
    return `${free}${disposable}${role}`;
  }

  if (locale === "tr") {
    const free = data.is_free ? "ücretsiz, " : "özel, ";
    const disposable = data.is_disposable ? "tek kullanımlık, " : "kalıcı, ";
    const role = data.is_role_based ? "rol tabanlı" : "rol tabanlı değil";
    return `${free}${disposable}${role}`;
  }

  if (locale === "de") {
    const free = data.is_free ? "kostenlos, " : "individuell, ";
    const disposable = data.is_disposable ? "Wegwerf-, " : "dauerhaft, ";
    const role = data.is_role_based ? "rollenbasiert" : "nicht rollenbasiert";
    return `${free}${disposable}${role}`;
  }

  if (locale === "es") {
    const free = data.is_free ? "gratuito, " : "personalizado, ";
    const disposable = data.is_disposable ? "temporal, " : "permanente, ";
    const role = data.is_role_based ? "basado en rol" : "no basado en rol";
    return `${free}${disposable}${role}`;
  }

  if (locale === "fr") {
    const free = data.is_free ? "gratuit, " : "personnalisé, ";
    const disposable = data.is_disposable ? "jetable, " : "permanent, ";
    const role = data.is_role_based ? "basé sur un rôle" : "sans rôle";
    return `${free}${disposable}${role}`;
  }

  if (locale === "pt") {
    const free = data.is_free ? "gratuito, " : "personalizado, ";
    const disposable = data.is_disposable ? "descartável, " : "permanente, ";
    const role = data.is_role_based ? "baseado em função" : "sem função";
    return `${free}${disposable}${role}`;
  }

  if (locale === "ru") {
    const free = data.is_free ? "бесплатный, " : "пользовательский, ";
    const disposable = data.is_disposable ? "одноразовый, " : "постоянный, ";
    const role = data.is_role_based ? "ролевой" : "не ролевой";
    return `${free}${disposable}${role}`;
  }

  const free = data.is_free ? "free, " : "custom, ";
  const disposable = data.is_disposable
    ? "disposable, and "
    : "non-disposable, and ";
  const role = data.is_role_based ? "role-based" : "not role-based";
  return `${free}${disposable}${role}`;
}

export {
  getToolResultData,
  hasToolResultPayload,
} from "@/lib/tools/tool-response";
